import { useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * RulesCard — renders the "How to Play" panel from the /blackjack/rules
 * backend endpoint. Consumed by both the modal on the table page and any
 * standalone rules chip elsewhere in the app.
 */
export default function RulesCard() {
    const [rules, setRules] = useState(null);
    useEffect(() => {
        axios.get(`${API}/blackjack/rules`, { withCredentials: true })
            .then(({ data }) => setRules(data))
            .catch(() => setRules({ sections: [] }));
    }, []);

    if (!rules) return <p className="text-[#8fbea4] text-sm py-6 text-center">Loading rules…</p>;

    return (
        <div className="space-y-3" data-testid="bj-rules-content">
            <div className="rounded-lg bg-[#FFD700]/10 border border-[#FFD700]/30 p-3 text-center">
                <p className="text-[#FFD700] font-black">
                    Blackjack pays <span className="text-lg">3 : 2</span> · Dealer stands on all 17s
                </p>
                <p className="text-[10px] text-[#c9e5d4] mt-1">
                    Min bet ${rules.min_bet?.toFixed(2)} · Max ${rules.max_bet?.toFixed(2)} per hand · Up to {rules.max_hands} hands
                </p>
            </div>
            {rules.beto_bonus && (
                <div className="rounded-lg bg-gradient-to-b from-[#5a1414]/60 to-[#3a0808]/60 border border-[#FFD700]/40 p-3" data-testid="bj-rules-beto">
                    <p className="text-[#FFD700] font-black text-sm mb-1 tracking-wider">BETO BONUS · SIDE BET</p>
                    <p className="text-[10px] text-[#c9e5d4] mb-2">
                        Optional ${rules.beto_bonus.min?.toFixed(2)}–${rules.beto_bonus.max?.toFixed(2)} bet
                        on each hand's first two cards + the dealer's up-card. Side bet cannot exceed that hand's main bet.
                    </p>
                    <div className="space-y-0.5">
                        {rules.beto_bonus.payouts.map((p) => (
                            <div key={p.key} className="flex items-center justify-between text-xs font-mono">
                                <span className="text-white">{p.label}</span>
                                <span className="font-black text-[#FFD700]">{p.multiplier} : 1</span>
                            </div>
                        ))}
                    </div>
                    <p className="text-[9px] text-[#8fbea4] italic mt-2">Loses if no qualifying combo.</p>
                </div>
            )}
            {rules.match_dealer && (
                <div className="rounded-lg bg-gradient-to-b from-[#003340]/60 to-[#001a20]/60 border border-[#00E1FF]/40 p-3" data-testid="bj-rules-mtd">
                    <p className="text-[#00E1FF] font-black text-sm mb-1 tracking-wider">MATCH THE DEALER · +M</p>
                    <p className="text-[10px] text-[#c9e5d4] mb-2">
                        Nested add-on to the Beto Bonus (${rules.match_dealer.min?.toFixed(2)}–${rules.match_dealer.max?.toFixed(2)}, capped at that hand's Beto Bonus amount).
                        Pays per rank match between either of your first two cards and the dealer's up-card.
                    </p>
                    <div className="space-y-0.5">
                        {rules.match_dealer.payouts.map((p) => (
                            <div key={p.key} className="flex items-center justify-between text-xs font-mono">
                                <span className="text-white">{p.label}</span>
                                <span className="font-black text-[#00E1FF]">{p.multiplier} : 1</span>
                            </div>
                        ))}
                    </div>
                    <p className="text-[9px] text-[#8fbea4] italic mt-2">Matches stack. Requires an active Beto Bonus.</p>
                </div>
            )}
            {rules.sections.map((s, i) => (
                <div key={i} className="border border-white/5 rounded-lg p-3 bg-[#0e2418]" data-testid={`bj-rules-section-${i}`}>
                    <p className="text-sm font-bold text-[#FFD700] mb-1">{s.heading}</p>
                    <p className="text-xs text-[#c9e5d4] leading-relaxed">{s.text}</p>
                </div>
            ))}
            <p className="text-[10px] text-[#8fbea4] italic text-center">
                Play responsibly. Set a budget and stick to it.
            </p>
        </div>
    );
}
